class PredictiveSearch extends Theme.Component {
  constructor() {
    super();

    this.modal = this.querySelector('[data-predictive-search-modal]');
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearch = this.querySelector('[data-predictive-search]');
    this.defaultState = this.querySelector('[data-predictive-search-default]');
    this.resultsBody = this.querySelector('[data-predictive-search-body]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.loadingText = this.getAttribute('data-loading-text');
    this.predictiveSearchUrl = this.dataset.predictiveSearchUrl;
    this.searchUrl = this.dataset.searchUrl;
    this.cachedResults = {};
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.abortController = null;
    this.requestId = 0;
    this.previouslyFocusedElement = null;

    this.handleShow = () => this.show();
    this.handleHide = () => this.hide();
    this.handleKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {

    super.connectedCallback();
    document.addEventListener("searchlink:click", this.handleShow);
    document.addEventListener("predictivesearch:close", this.handleHide);
    this.addEventListener('keydown', this.handleKeydown);

    const closeButton = this.querySelector('[data-predictive-modal-close]');
    if (closeButton) {
      closeButton.addEventListener("click", () => this.hide());
    }

    const overlay = this.querySelector('[data-predictive-search-overlay]');
    if (overlay) {
      overlay.addEventListener("click", () => this.hide());
    }

    const clearButton = this.querySelector('button[data-predictive-search-clear]');
    if (clearButton && this.input) {
      clearButton.addEventListener('click', () => {
        this.cancelPendingRequest();
        this.input.value = '';
        this.searchTerm = '';
        this.predictiveSearch.innerHTML = '';
        this.setDefaultStateVisible(true);
        this.endLoading();
        this.scheduleCardAlignment();
        this.input.focus();
      });
    }

    if (this.input) {
      this.input.addEventListener('input', Theme.Helpers.debounce(this.onChange.bind(this), 300));
    }

    this.addEventListener('focusout', this.onFocusOut.bind(this));

    if (this.predictiveSearch) {
      this.predictiveSearch.addEventListener('click', () => {
        if (this.searchTerm && Theme.Activity) Theme.Activity.recordSearch(this.searchTerm);
      });
    }
  }

  disconnectedCallback() {

    super.disconnectedCallback();
    document.removeEventListener("searchlink:click", this.handleShow);
    document.removeEventListener("predictivesearch:close", this.handleHide);
    this.removeEventListener('keydown', this.handleKeydown);
    this.cancelPendingRequest();
    clearTimeout(this.statusTimer);
  }

  onChange(event) {
    const newSearchTerm = event.target.value.trim();

    if (newSearchTerm.length) this.beginLoading();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.predictiveSearch.innerHTML = '';
    }

    this.searchTerm = newSearchTerm;

    if (this.searchTerm.length) {
      this.setDefaultStateVisible(false);
      this.getSearchResults(this.searchTerm);
    } else {
      this.cancelPendingRequest();
      this.predictiveSearch.innerHTML = '';
      this.setDefaultStateVisible(true);
      this.endLoading();
      this.scheduleCardAlignment();
    }
  }

  setDefaultStateVisible(visible) {
    if (!this.defaultState) return;
    this.defaultState.hidden = !visible;
  }

  beginLoading() {
    if (!this.resultsBody) return;
    this.resultsBody.classList.add('state--loading');
  }

  endLoading() {
    if (!this.resultsBody) return;
    this.resultsBody.classList.remove('state--loading');
  }

  getSearchResults(query) {
    const queryKey = query.toLowerCase();
    this.cancelPendingRequest();
    this.setLiveRegionText(this.loadingText);

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (!this.predictiveSearchUrl) return;

    const requestId = ++this.requestId;
    this.abortController = new AbortController();
    const params = new URLSearchParams({
      q: query,
      'resources[limit]': '10',
      section_id: 'predictive-search',
    });
    const url = `${this.predictiveSearchUrl}?${params.toString()}`;

    fetch(url, { signal: this.abortController.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Fetch failed with status ${res.status}`);
        return res.text();
      })
      .then((html) => {
        if (requestId !== this.requestId || query !== this.searchTerm) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const results = doc.querySelector('#shopify-section-predictive-search');
        if (results) {
          this.allPredictiveSearchInstances.forEach((instance) => {
            instance.cachedResults[queryKey] = results.innerHTML;
          });
          this.renderSearchResults(results.innerHTML);
        } else {
          this.endLoading();
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Predictive search failed', err);
          this.endLoading();
        }
      });
  }

  cancelPendingRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.requestId += 1;
  }

  renderSearchResults(resultsMarkup) {
    this.setDefaultStateVisible(false);
    this.predictiveSearch.innerHTML = resultsMarkup;
    this.endLoading();

    const count = this.predictiveSearch.querySelectorAll('.predictive-search-list-item').length;
    this.setLiveRegionText(
      count
        ? `${count} result${count === 1 ? '' : 's'} for ${this.searchTerm}`
        : `No results for ${this.searchTerm}`
    );

    this.scheduleCardAlignment();

    const allResultsButton = this.querySelector('[data-predictive-all-results]');
    if (allResultsButton && this.input) {
      allResultsButton.addEventListener('click', () => {
        const query = this.input.value.trim();

        if (query.length) {
          const params = new URLSearchParams({ q: query, type: 'product' });
          window.location.href = `${this.searchUrl}?${params.toString()}`;
        }
      });
    }
  }

  scheduleCardAlignment() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof alignProductCardsHeight === 'function') alignProductCardsHeight(this);
      });
    });
  }

  show() {
    if (this.modal) {
      if (!this.classList.contains("state--active")) {
        this.previouslyFocusedElement = document.activeElement;
      }
      this.modal.classList.add("visible");
      this.classList.add("state--active");
      document.body.classList.add("no-scroll");
      this.setDefaultStateVisible(!this.searchTerm);
      this.scheduleCardAlignment();
      if (this.input) this.input.focus();
    }
  }

  hide(restoreFocus = true) {
    if (this.modal) {
      this.cancelPendingRequest();
      this.endLoading();
      this.modal.classList.remove("visible");
      this.classList.remove("state--active");
      document.body.classList.remove("no-scroll");

      if (restoreFocus && this.previouslyFocusedElement?.isConnected) {
        this.previouslyFocusedElement.focus();
      }
    }
  }

  getFocusableElements() {
    if (!this.modal) return [];

    return Array.from(
      this.modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => element.getClientRects().length > 0);
  }

  getResultControls() {
    return this.getFocusableElements().filter((element) => element !== this.input && !element.hasAttribute('data-predictive-modal-close') && !element.hasAttribute('data-predictive-search-clear'));
  }

  onKeydown(event) {
    if (!this.classList.contains("state--active")) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.hide();
      return;
    }

    if (event.key === 'Tab') {
      const focusableElements = this.getFocusableElements();
      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const resultControls = this.getResultControls();
    if (!resultControls.length) return;

    const activeIndex = resultControls.indexOf(document.activeElement);
    let nextIndex;

    if (event.key === 'ArrowDown') {
      nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % resultControls.length;
    } else {
      nextIndex = activeIndex < 0 ? resultControls.length - 1 : (activeIndex - 1 + resultControls.length) % resultControls.length;
    }

    event.preventDefault();
    resultControls[nextIndex].focus();
  }
  setLiveRegionText(text) {
    if (!this.statusElement) return;
    clearTimeout(this.statusTimer);
    this.statusElement.textContent = text;
    this.statusTimer = setTimeout(() => {
      this.statusElement.textContent = '';
    }, 1000);
  }

  onFocusOut(event) {
    setTimeout(() => {
      const activeEl = document.activeElement;

      const isModalStillFocused = this.contains(activeEl);
      const isFocusNowOnBody = activeEl === document.body;

      if (!isModalStillFocused && !isFocusNowOnBody) {
        this.hide(false);
      }
    });
  }
}

customElements.define("predictive-search", PredictiveSearch);
